import { CSSProperties, FC, ReactElement, useEffect, useMemo } from 'react';
import { IconButton, SvgIcon, TypographyProps } from '@mui/material';

import { TMoplaColors } from '@mopla/mui';

import { NativePageBackground } from '../../atoms/NativePageBackground/NativePageBackground';
import { CrossIcon, LeftChevronIcon } from '../../icons';
import { useStatusBarStyleSync } from '../../organisms/StatusBarStyleSyncProvider/context';
import { useIsInBottomSlider } from '../BottomSlider/context';

import { variantConfigs } from './config';
import { ChildrenWrapper, IOwnerState, Root, Title } from './styled';

export type TMplPageHeaderVariant =
	| 'primary'
	| 'primaryDark'
	| 'primary_light'
	| 'secondary'
	| 'neutral.0';

export interface IMplPageHeaderProps {
	className?: string;
	open: boolean;
	showBack?: boolean;
	showClose?: boolean;
	onBack?: () => void;
	onClose?: () => void;
	variant?: TMplPageHeaderVariant;
	title: string;
	children?: ReactElement | null;
	size?: 'small' | 'medium';
	color?: TMoplaColors | CSSProperties['color'];
}

export const MplPageHeader: FC<IMplPageHeaderProps> = ({
	className,
	open,
	showBack = false,
	showClose = false,
	onBack,
	onClose,
	variant = 'primary',
	title,
	children,
	size,
	color,
}) => {
	const hasChildren = Boolean(children);
	const variantConfig = variantConfigs[variant];
	const isInBottomSlider = useIsInBottomSlider();

	const withIcons = showBack || showClose;

	const ownerState = useMemo<IOwnerState>(
		() => ({
			variant,
			rounded: Boolean(title || hasChildren),
			withIcons,
		}),
		[hasChildren, title, variant, withIcons]
	);

	const { push, pop } = useStatusBarStyleSync();

	/**
	 * Adapts the StatusBar style when the MplPageHeader is opened inside a BottomSlider.
	 * It is important that `isInBottomSlider` is true, as the MplPageHeader is underneath the StatusBar in this case.
	 * */
	useEffect(() => {
		if (open && isInBottomSlider && variantConfig?.statusBarColor) {
			push({
				title,
				style: variantConfig.statusBarColor,
			});

			return () => {
				pop();
			};
		}

		return;
	}, [open, isInBottomSlider, variantConfig, push, title, pop]);

	const getTitleVariant = (): TypographyProps['variant'] =>
		size === 'small' ? 'h3-sb' : 'h2-sb';

	return (
		<>
			<Root
				className={className}
				ownerState={ownerState}
				sx={{
					color: color || variantConfig.textColor,
					backgroundColor: variantConfig.bgColor,
				}}
			>
				{showBack && (
					<IconButton
						onClick={onBack}
						data-testid="drawer-back-button"
						sx={{ color: 'inherit' }}
					>
						<SvgIcon>
							<LeftChevronIcon />
						</SvgIcon>
					</IconButton>
				)}
				{title && (
					<Title data-testid="drawer-header-text" variant={getTitleVariant()}>
						{title}
					</Title>
				)}
				{showClose && (
					<IconButton
						onClick={onClose}
						data-testid="drawer-close-button"
						sx={{ color: 'inherit' }}
					>
						<SvgIcon>
							<CrossIcon />
						</SvgIcon>
					</IconButton>
				)}
				{children ? <ChildrenWrapper>{children}</ChildrenWrapper> : null}
			</Root>
			{open && <NativePageBackground bgColor={variantConfig.bgColor} />}
		</>
	);
};
